import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Badge, Button, Collapse } from 'reactstrap';

import {
  addSelectedFilter,
  deleteSelectedFilter,
} from '../../actions/Files';

import { userIsAdmin } from '../../helpers/Auth';
import { hasTaxonomyRoleSettingAllowed } from '../../helpers/Metadata';
import { getSetting } from '../../helpers/Settings';

import FiltersResetButton from './FiltersResetButton';
import FiltersFileTerms from './FiltersFileTerms';

const FiltersFileMetadataGroup = ({
  filters,
  taxonomies,
  removeFilter,
  isOpen,
  metadataGroup,
  metadataGroups,
  handleChangeFilters,
  handleToggleFilter,
  openFilters,
  inPopover,
}) => {
  const filtersSetting = getSetting('filters_files');
  const metadataGroupFilters = [];
  const taxonomyFilters = [];

  const getSelectedChildren = () => {
    if (!filters || filters.length === 0) return [];

    const selectedChildren = [];

    const tg = metadataGroups.find(g => `${g.id}` === `${metadataGroup.id}`);
    if (!tg) return selectedChildren;

    tg.taxonomies.forEach(taxonomyId => {
      const tax = taxonomies.find(t => `${t.id}` === `${taxonomyId}`);

      tax.terms.forEach(term => {
        if (filters.includes(`${term.id}`)) {
          selectedChildren.push({
            value: `${term.id}`,
            label: term.name,
          });
        }
      });
    });

    return selectedChildren;
  };

  const selectedChildren = getSelectedChildren();

  const handleChangeFilter = (values, action) => {
    let newSelected = filters.slice();

    if (action.action === 'remove-value') {
      const { removedValue: value } = action;
      newSelected = newSelected.filter(s => (s !== value.value));
      removeFilter('terms', value.value);
    }

    if (action.action === 'clear') {
      selectedChildren.forEach(filter => {
        newSelected = newSelected.filter(s => (s !== filter.value));
        removeFilter('terms', filter.value);
      });
    }

    handleChangeFilters({ terms: newSelected });
  };

  const handleReset = () => {
    handleChangeFilter([], { action: 'clear' });
  };

  const isMetadataGroupEmpty = () => !filtersSetting.some(filter => {
    if (!filter.visible || (filter.admins_only && !userIsAdmin())) {
      return false;
    }

    let taxonomiesFilters = [];
    let childrenToShow = [];

    if (filter.key.includes(`tax_group_${metadataGroup.id}`)) {
      if (!Array.isArray(filter.children)) {
        return false;
      }

      taxonomiesFilters = metadataGroup.children ?
        metadataGroup.children.map(c => parseInt(c.key.replace(`tax_group_${metadataGroup.id}_tax_`, ''))) : [];

      childrenToShow =
        filter.children.filter(child => child.visible && !(child.admins_only && !userIsAdmin()));
    }

    metadataGroup.taxonomies.forEach(t => {
      if (!taxonomiesFilters.includes(t.id)) {
        taxonomiesFilters.push(t.id);
        childrenToShow.push(t.id);
      }
    });

    return childrenToShow.length > 0;
  });

  if (isMetadataGroupEmpty()) {
    return null;
  }

  let filter = filtersSetting.find(f => f.key === `tax_group_${metadataGroup.id}`);
  if (!filter) {
    filter = {
      key: `tax_group_${metadataGroup.id}`,
      name: `${metadataGroup.name}`,
      visible: true,
      children: metadataGroup.taxonomies.map(tid => {
        const taxonomy = taxonomies.find(t => t.id === tid);
        if (!taxonomy) return null;
        return {
          key: `tax_group_${metadataGroup.id}_tax_${taxonomy.id}`,
          name: `${taxonomy.name} (Tag Category)`,
          visible: hasTaxonomyRoleSettingAllowed(taxonomy, 'can_view'),
        };
      }).filter(Boolean),
    };
  }

  if (!filter.visible || (filter.admins_only && !userIsAdmin())) {
    if (filter.key && filter.key.indexOf(`tax_group_${metadataGroup.id}`) === 0) {
      metadataGroupFilters.push(parseInt(filter.key.replace('tax_group_', '')));
    }
    return null;
  }

  if (!_.isArray(filter.children)) return null;

  return (
    <div className="files-filter file-metadata-group-filter">
      <div className="filter-header">
        <Button
          onClick={() => handleToggleFilter(`tax_group_${metadataGroup.id}`)}
          className="filter-title"
        >
          <i className={`fal fa-angle-${isOpen ? 'up' : 'down'}`} />
          <i className="far fa-folder-open primary" />
          <span>{metadataGroup.name}</span>{' '}
          {selectedChildren.length > 0 && <Badge color="primary">{selectedChildren.length}</Badge>}
        </Button>
        {selectedChildren.length > 0 && <FiltersResetButton handleReset={handleReset} />}
      </div>
      <Collapse isOpen={isOpen} className="filter-body">
        <div className="filter-form file-metadata-group-filter-form">
          {filter.children.map(childrenFilter => {
            if (!childrenFilter.visible || (childrenFilter.admins_only && !userIsAdmin())) {
              if (childrenFilter.key && childrenFilter.key.indexOf(`tax_group_${metadataGroup.id}_`) === 0) {
                taxonomyFilters.push(parseInt(childrenFilter.key.replace('tax_group_', '')));
              }
              return null;
            }
            const taxonomyId = Number(
              childrenFilter.key.replace(`tax_group_${metadataGroup.id}_tax_`, ''),
            );
            const taxonomy = taxonomies.find(t => `${t.id}` === `${taxonomyId}`);

            if (!taxonomy) return null;

            taxonomyFilters.push(taxonomy.id);

            if (taxonomy.ai_tagging && !getSetting('ai_tagging')) {
              return null;
            }

            return (
              <FiltersFileTerms
                key={childrenFilter.key}
                isOpen={openFilters.includes(`tax_group_${metadataGroup.id}_tax_${taxonomy.id}`)}
                taxonomy={taxonomy}
                handleChangeFilters={handleChangeFilters}
                handleToggleFilter={() => handleToggleFilter(`tax_group_${metadataGroup.id}_tax_${taxonomy.id}`)}
                inPopover={inPopover}
              />
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

const mapStateToProps = (state, props) => ({
  filters: props.customFilters ? props.customFilters.terms : state.Files.filters.terms,
  taxonomies: state.Metadata.taxonomies,
  metadataGroups: state.Metadata.metadataGroups,
  availableFilters: props.customAvailableFilters || state.Files.availableFilters,
});

const mapDispatchToProps = (dispatch, props) => ({
  addFilter: props.customAddFilter || ((filter) => dispatch(addSelectedFilter(filter))),
  removeFilter: props.customRemoveFilter ||
    ((filterType, filterKey) => dispatch(deleteSelectedFilter(filterType, filterKey))),
});

FiltersFileMetadataGroup.propTypes = {
  filters: PropTypes.array.isRequired,
  openFilters: PropTypes.array.isRequired,
  taxonomies: PropTypes.array.isRequired,
  metadataGroups: PropTypes.array.isRequired,
  removeFilter: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  metadataGroup: PropTypes.object.isRequired,
  handleChangeFilters: PropTypes.func.isRequired,
  handleToggleFilter: PropTypes.func.isRequired,
  inPopover: PropTypes.bool,
};

FiltersFileMetadataGroup.defaultProps = {
  inPopover: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(FiltersFileMetadataGroup);
